import type {
  AskUserAnswer,
  ResolvedDecision,
} from "../types/episode.ts";
import {
  ASK_USER_MARKER_CLOSER,
  ASK_USER_MARKER_PREFIX,
  buildAskUserPairings,
  isAskUserName,
  type ApprovalReadState,
} from "./queue.ts";

function parseAskUserAnswerText(text: string): AskUserAnswer[] {
  const lines = text.split("\n");
  const body: string[] = [];
  for (const line of lines) {
    if (line.trim() === "") continue;
    if (line.startsWith(ASK_USER_MARKER_PREFIX)) continue;
    if (line.startsWith(ASK_USER_MARKER_CLOSER)) continue;
    body.push(line);
  }
  const answers: AskUserAnswer[] = [];
  for (const line of body) {
    const sep = line.indexOf(": ");
    if (sep > 0) {
      answers.push({
        question: line.slice(0, sep),
        answer: line.slice(sep + 2),
      });
    } else {
      answers.push({ answer: line });
    }
  }
  return answers;
}

/**
 * Does this tool_result look like an immutable mutation outcome that the
 * chat feed should render as a static summary card (DecisionSummary +
 * EntityCardRenderer) rather than via the module's live renderer?
 *
 * Two shapes qualify:
 *  1. The kind-differentiated envelope produced by mutating tool handlers:
 *     `{ kind: "created" | "updated", id, schema_id, ... }`.
 *  2. Legacy/pre-envelope entity-shaped results with a top-level `id` —
 *     these are what create/update tools returned before the envelope
 *     rollout and are still what `classify_tool_result` on the backend
 *     keys off.
 *
 * Read tools return arrays/`items` lists with no top-level `id`, so they
 * fall through and keep their module feed renderer.
 */
function isMutationResult(result: unknown): boolean {
  if (result === null || typeof result !== "object") return false;
  const r = result as Record<string, unknown>;
  if (typeof r.kind === "string" && (r.kind === "created" || r.kind === "updated")) {
    return true;
  }
  return typeof r.id === "string" && r.id.length > 0;
}

/**
 * Derive a ResolvedDecision for a given tool call from the already-persisted
 * episode state. The approval summary is status-only (the backend does not
 * persist executed-result messages — see chat.rs and AgentChatStore
 * reconstruction). The ask_user summary pairs the originating tool_call with
 * its per-id answering user_message block, so interleaved multi-turn
 * conversations resolve independently.
 *
 * Approval decisions are returned for:
 *  - tool calls that went through a real approval flow (have `approvalId`), or
 *  - completed tool calls whose `tool_result` is a mutation envelope or an
 *    entity-shaped return with a top-level `id` (see `isMutationResult`).
 *
 * The second branch makes the chat render mutation outcomes as the
 * immutable, static summary card even when approval was bypassed
 * (`DISABLE_TOOL_APPROVAL=1`) or auto-granted via allowlist. Plain read
 * tools (lists, gets) still fall through to their module renderer.
 *
 * Returns `null` when the tool call has not been resolved yet (still
 * pending), when the id is unknown, or when no pairing exists.
 */
export function resolveDecisionForToolCall(
  state: ApprovalReadState,
  toolCallId: string,
): ResolvedDecision | null {
  const tc = state.toolCalls.find((t) => t.id === toolCallId);
  if (!tc) return null;

  if (isAskUserName(tc.name)) {
    const pairings = buildAskUserPairings(state);
    const answerText = pairings.get(toolCallId);
    if (answerText === undefined) return null;
    return {
      kind: "ask_user",
      toolCallId,
      answerText,
      answers: parseAskUserAnswerText(answerText),
    };
  }

  if (tc.status !== "approved" && tc.status !== "denied") return null;

  const toolResult = state.toolResults.find((tr) => tr.id === toolCallId);

  // Either a real approval decision, or a completed mutation whose result
  // is envelope/entity-shaped — both render as the static summary card.
  const isApprovalGated = tc.approvalId !== undefined;
  const isMutationOutcome = isMutationResult(toolResult?.result);
  if (!isApprovalGated && !isMutationOutcome) return null;

  return {
    kind: "approval",
    toolCallId,
    status: tc.status,
    toolName: tc.name,
    args: tc.args,
    result: toolResult?.result,
  };
}
